// @ts-nocheck
/**
 * Algo Conditions Page
 *
 * Conditions mirror the Quick Order Entry (QOE) table in AlgoTrading:
 *   Exchange → Symbol → Row (CE/PE) → Column (Strength / LTP / Dif)
 *   → Operator → Value → Action (BUY / SELL)
 *
 * AlgoTrading.js writes live QOE values into localStorage key "qoe_live" on
 * every render.  This page polls that key every 500 ms and evaluates conditions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

// ── Static option lists ───────────────────────────────────────────────────────

const EXCHANGES = ['NFO', 'BFO', 'MCX', 'NSE'];

const SYMBOLS_BY_EXCHANGE = {
  NFO: ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY', 'NIFTYNXT50'],
  BFO: ['SENSEX', 'BANKEX'],
  MCX: ['CRUDEOIL', 'GOLD', 'SILVER', 'COPPER', 'NATURALGAS', 'ZINC', 'LEAD', 'ALUMINIUM'],
  NSE: ['CUSTOM'],
};

const COLUMNS = [
  { value: 'strength', label: 'Strength', unit: '%',  hint: 'MTM % = (currentSum − baselineSum) / 10' },
  { value: 'ltp',      label: 'LTP',      unit: '₹',  hint: 'Last Traded Price of the selected CE/PE strike' },
  { value: 'dif',      label: 'Dif',      unit: '₹',  hint: 'LTP − Entry Price at the time the strike was added' },
];

const OPERATORS = [
  { value: '<=', label: '≤  (less than or equal)' },
  { value: '>=', label: '≥  (greater than or equal)' },
  { value: '<',  label: '<  (less than)' },
  { value: '>',  label: '>  (greater than)' },
];

const CONDITION_TYPES = [
  { value: 'BUY',  label: 'Buy',  hint: 'Only executes when Algo is set to "Buy Conditions" or "Both"' },
  { value: 'SELL', label: 'Sell', hint: 'Only executes when Algo is set to "Sell Conditions" or "Both"' },
];

const EMPTY_FORM = {
  name: '',
  conditionType: 'BUY',
  exchange: 'NFO',
  symbol: 'NIFTY',
  rowType: 'CE',
  columnName: 'strength',
  operator: '<=',
  triggerValue: '',
  action: 'BUY',
  orderType: 'MARKET',
  product: 'MIS',
  triggerOnce: true,
  cooldownSeconds: 60,
  // Stop Loss
  slEnabled:   false,
  slColumn:    'strength',
  slOperator:  '>=',
  slValue:     '',
  // Profit Booking
  profitEnabled:   false,
  profitColumn:    'strength',
  profitOperator:  '<=',
  profitValue:     '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const evalCondition = (current, operator, threshold) => {
  switch (operator) {
    case '<=': return current <= threshold;
    case '>=': return current >= threshold;
    case '<':  return current < threshold;
    case '>':  return current > threshold;
    default:   return false;
  }
};

const colLabel  = (v) => COLUMNS.find((c) => c.value === v)?.label  || v;
const colUnit   = (v) => COLUMNS.find((c) => c.value === v)?.unit   || '';

const statusBadge = (cond) => {
  if (cond.is_triggered) return { text: 'Triggered', cls: 'bg-blue-100 text-blue-800 border-blue-300' };
  if (!cond.is_active)   return { text: 'Inactive',  cls: 'bg-gray-100 text-gray-500 border-gray-300' };
  return                        { text: 'Watching',  cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
};

// ── Component ─────────────────────────────────────────────────────────────────

const ALGO_PAGE_PASSWORD = 'Admin@@##2026';

const AlgoConditions = () => {
  const { userId } = useSelector((s) => s.auth);

  // ── password gate ──────────────────────────────────────────────────────────
  const [unlocked,   setUnlocked]   = useState(() => sessionStorage.getItem('algoConditionsUnlocked') === '1');
  const [pwInput,    setPwInput]    = useState('');
  const [pwError,    setPwError]    = useState('');
  const [pwVisible,  setPwVisible]  = useState(false);

  // ── global state ───────────────────────────────────────────────────────────
  const [algoEnabled, setAlgoEnabled] = useState(
    () => localStorage.getItem('algoTrading_enabled') === 'true'
  );
  const [algoMode, setAlgoMode] = useState(
    () => localStorage.getItem('algoTrading_mode') || 'BOTH'
  );
  const [showAlgoModeDropdown, setShowAlgoModeDropdown] = useState(false);

  // ── conditions ─────────────────────────────────────────────────────────────
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading]       = useState(false);

  // ── modal ──────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [formErr,   setFormErr]   = useState('');

  // ── reference conditions ───────────────────────────────────────────────────
  const [showRefModal,   setShowRefModal]   = useState(false);
  const [refConditions,  setRefConditions]  = useState([]);
  const [refLoading,     setRefLoading]     = useState(false);
  const [copyingId,      setCopyingId]      = useState(null);

  // ── live QOE values (read from localStorage via polling) ─────────────────
  const [qoe, setQoe] = useState(null);   // the whole qoe_live object

  // ── execution logs (DB-backed) ─────────────────────────────────────────────
  const [execLogs,     setExecLogs]     = useState([]);
  const [logFilter,    setLogFilter]    = useState('today'); // 'today' | 'week' | 'all'
  const [logsLoading,  setLogsLoading]  = useState(false);

  const fetchLogs = useCallback(async (filter) => {
    if (!userId) return;
    setLogsLoading(true);
    try {
      const res = await api.get(`/algo-conditions/logs?userId=${userId}&filter=${filter}`);
      if (res.data.success) setExecLogs(res.data.data);
    } catch (_) {}
    setLogsLoading(false);
  }, [userId]);

  useEffect(() => { fetchLogs(logFilter); }, [fetchLogs, logFilter]);

  /** Save one execution log entry to DB and prepend to local state */
  const addToLog = useCallback(async (entry) => {
    // Prepend to local list immediately for instant UI
    setExecLogs((prev) => [{ ...entry, executed_at: new Date().toISOString() }, ...prev]);
    // Persist to DB
    try {
      await api.post('/algo-conditions/logs', {
        userId,
        conditionId:   entry.conditionId   ?? null,
        conditionName: entry.name,
        exchange:      entry.exchange,
        symbol:        entry.symbol,
        rowType:       entry.rowType,
        columnName:    entry.col,
        columnValue:   entry.value != null ? parseFloat(entry.value) : null,
        operator:      entry.operator      ?? null,
        triggerValue:  entry.triggerValue  ?? null,
        action:        entry.action,
        quantity:      entry.qty           ?? null,
        tradingSymbol: entry.tradingSymbol ?? null,
        orderId:       entry.orderId       ?? null,
        status:        entry.status,
        errorMessage:  entry.error         ?? null,
      });
    } catch (_) {}
  }, [userId]);

  // ── popup ──────────────────────────────────────────────────────────────────
  const [popup, setPopup] = useState({ show: false, type: 'success', msg: '' });

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const showPopup = useCallback((type, msg) => {
    setPopup({ show: true, type, msg });
    setTimeout(() => setPopup((p) => ({ ...p, show: false })), 4500);
    // Broadcast to AlgoTrading page (cross-tab via localStorage, same-tab via CustomEvent)
    const notification = { type, msg, ts: Date.now() };
    try {
      localStorage.setItem('algo_condition_notification', JSON.stringify(notification));
      window.dispatchEvent(new CustomEvent('algo_condition_notification', { detail: notification }));
    } catch (_) {}
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Algo toggle (shared via localStorage with AlgoTrading page)
  // ─────────────────────────────────────────────────────────────────────────
  const toggleAlgo = () => {
    if (algoEnabled) {
      setAlgoEnabled(false);
      setShowAlgoModeDropdown(false);
      localStorage.setItem('algoTrading_enabled', 'false');
    } else {
      setShowAlgoModeDropdown((prev) => !prev);
    }
  };

  const enableAlgoWithMode = (mode) => {
    setAlgoMode(mode);
    setAlgoEnabled(true);
    setShowAlgoModeDropdown(false);
    localStorage.setItem('algoTrading_mode', mode);
    localStorage.setItem('algoTrading_enabled', 'true');
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'algoTrading_enabled') setAlgoEnabled(e.newValue === 'true');
      if (e.key === 'algoTrading_mode')    setAlgoMode(e.newValue || 'BOTH');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Poll localStorage for live QOE values written by AlgoTrading.js
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const poll = () => {
      try {
        const raw = localStorage.getItem('qoe_live');
        if (raw) setQoe(JSON.parse(raw));
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 500);
    return () => clearInterval(id);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Load conditions from backend
  // ─────────────────────────────────────────────────────────────────────────
  const loadConditions = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/algo-conditions?userId=${userId}`);
      if (res.data.success) setConditions(res.data.data);
    } catch (err) {
      showPopup('error', 'Failed to load conditions: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, showPopup]);

  useEffect(() => { loadConditions(); }, [loadConditions]);

  // Reload conditions when background execution engine fires / resets
  useEffect(() => {
    const onExecEvent = () => {
      loadConditions();
      fetchLogs(logFilter);
    };
    window.addEventListener('algo_execution_event', onExecEvent);
    return () => window.removeEventListener('algo_execution_event', onExecEvent);
  }, [loadConditions, fetchLogs, logFilter]);

  // ─────────────────────────────────────────────────────────────────────────
  // QOE preview helpers (UI display only — execution runs in AlgoExecutionEngine)
  // ─────────────────────────────────────────────────────────────────────────

  /** Returns the current value for a condition's row+column, or null if unavailable */
  const getCurrentValue = useCallback((cond) => {
    if (!qoe) return null;
    // Only evaluate when AlgoTrading is showing the matching exchange+symbol
    if (
      qoe.activeExchange?.toUpperCase() !== cond.exchange?.toUpperCase() ||
      qoe.activeSymbol?.toUpperCase()   !== cond.symbol?.toUpperCase()
    ) return null;

    const row = cond.row_type === 'CE' ? 'ce' : 'pe';
    switch (cond.column_name) {
      case 'strength': return qoe[`${row}Strength`] ?? null;
      case 'ltp':      return qoe[`${row}LTP`]      ?? null;
      case 'dif':      return qoe[`${row}Dif`]      ?? null;
      default:         return null;
    }
  }, [qoe]);

  // ─────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErr('');
    setShowModal(true);
  };

  const openEdit = (cond) => {
    setEditingId(cond.id);
    setForm({
      name:            cond.name,
      conditionType:   (cond.condition_type === 'BOTH' || !cond.condition_type) ? cond.action : cond.condition_type,
      exchange:        cond.exchange,
      symbol:          cond.symbol,
      rowType:         cond.row_type,
      columnName:      cond.column_name,
      operator:        cond.operator,
      triggerValue:    cond.trigger_value,
      action:          cond.action,
      orderType:       cond.order_type,
      product:         cond.product,
      triggerOnce:     !!cond.trigger_once,
      cooldownSeconds: cond.cooldown_seconds ?? 60,
      slEnabled:       !!cond.sl_enabled,
      slColumn:        cond.sl_column    || 'strength',
      slOperator:      cond.sl_operator  || '>=',
      slValue:         cond.sl_value     ?? '',
      profitEnabled:   !!cond.profit_enabled,
      profitColumn:    cond.profit_column   || 'strength',
      profitOperator:  cond.profit_operator || '<=',
      profitValue:     cond.profit_value    ?? '',
    });
    setFormErr('');
    setShowModal(true);
  };

  const saveCondition = async () => {
    setFormErr('');
    if (!form.name.trim())   return setFormErr('Name is required.');
    if (!form.triggerValue)  return setFormErr('Trigger value is required.');
    if (form.slEnabled     && (form.slValue     === '' || form.slValue     == null)) return setFormErr('Stop Loss value is required.');
    if (form.profitEnabled && (form.profitValue === '' || form.profitValue == null)) return setFormErr('Profit value is required.');

    setSaving(true);
    try {
      const payload = {
        userId,
        name:            form.name.trim(),
        conditionType:   form.conditionType || 'BOTH',
        exchange:        form.exchange,
        symbol:          form.symbol,
        rowType:         form.rowType,
        columnName:      form.columnName,
        operator:        form.operator,
        triggerValue:    parseFloat(form.triggerValue),
        action:          form.action,
        orderType:       form.orderType,
        product:         form.product,
        isActive:        true,
        triggerOnce:     form.triggerOnce,
        cooldownSeconds: parseInt(form.cooldownSeconds || 0),
        slEnabled:       form.slEnabled,
        slColumn:        form.slColumn,
        slOperator:      form.slOperator,
        slValue:         form.slEnabled && form.slValue !== '' ? parseFloat(form.slValue) : null,
        profitEnabled:   form.profitEnabled,
        profitColumn:    form.profitColumn,
        profitOperator:  form.profitOperator,
        profitValue:     form.profitEnabled && form.profitValue !== '' ? parseFloat(form.profitValue) : null,
      };

      if (editingId) {
        const res = await api.put(`/algo-conditions/${editingId}`, payload);
        if (res.data.success)
          setConditions((p) => p.map((c) => (c.id === editingId ? res.data.data : c)));
        showPopup('success', 'Condition updated.');
      } else {
        const res = await api.post('/algo-conditions', payload);
        if (res.data.success) setConditions((p) => [res.data.data, ...p]);
        showPopup('success', 'Condition created.');
      }
      window.dispatchEvent(new CustomEvent('algo_conditions_changed'));
      setShowModal(false);
    } catch (err) {
      setFormErr(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const loadReference = async () => {
    setRefLoading(true);
    try {
      const res = await api.get(`/algo-conditions/reference?userId=${userId}`);
      if (res.data.success) setRefConditions(res.data.data);
    } catch (err) {
      showPopup('error', 'Failed to load reference conditions: ' + err.message);
    }
    setRefLoading(false);
  };

  const notifyEngine = () => window.dispatchEvent(new CustomEvent('algo_conditions_changed'));

  const copyCondition = async (cond) => {
    setCopyingId(cond.id);
    try {
      const res = await api.post('/algo-conditions/copy', { userId, conditionId: cond.id });
      if (res.data.success) {
        setConditions((p) => [res.data.data, ...p]);
        showPopup('success', `"${cond.name}" copied to your conditions.`);
        notifyEngine();
      }
    } catch (err) {
      showPopup('error', 'Copy failed: ' + err.message);
    }
    setCopyingId(null);
  };

  const deleteCondition = async (id) => {
    if (!window.confirm('Delete this condition?')) return;
    try {
      await api.delete(`/algo-conditions/${id}?userId=${userId}`);
      setConditions((p) => p.filter((c) => c.id !== id));
      showPopup('success', 'Deleted.');
      notifyEngine();
    } catch (err) {
      showPopup('error', 'Delete failed: ' + err.message);
    }
  };

  const toggleActive = async (cond) => {
    try {
      const res = await api.post(`/algo-conditions/${cond.id}/toggle`, { userId });
      if (res.data.success) {
        setConditions((p) => p.map((c) => (c.id === cond.id ? res.data.data : c)));
        notifyEngine();
      }
    } catch (err) {
      showPopup('error', 'Toggle failed: ' + err.message);
    }
  };

  const resetCondition = async (cond) => {
    try {
      const res = await api.post(`/algo-conditions/${cond.id}/reset`, { userId });
      if (res.data.success) {
        setConditions((p) => p.map((c) => (c.id === cond.id ? res.data.data : c)));
        showPopup('success', `"${cond.name}" reset — watching again.`);
        notifyEngine();
      }
    } catch (err) {
      showPopup('error', 'Reset failed: ' + err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived data for the live QOE preview card
  // ─────────────────────────────────────────────────────────────────────────
  const qoeAge = qoe ? Math.floor((Date.now() - qoe.ts) / 1000) : null;
  const qoeFresh = qoeAge !== null && qoeAge < 5;

  const fmtQoeVal = (v, col) => {
    if (v === null || v === undefined) return '—';
    const n = parseFloat(v);
    return col === 'strength' ? `${n >= 0 ? '+' : ''}${n.toFixed(1)}%` : `₹${n.toFixed(2)}`;
  };

  const stats = {
    total:     conditions.length,
    watching:  conditions.filter((c) => c.is_active && !c.is_triggered).length,
    triggered: conditions.filter((c) => c.is_triggered).length,
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  Password gate
  // ─────────────────────────────────────────────────────────────────────────
  if (!unlocked) {
    const handleUnlock = () => {
      if (pwInput === ALGO_PAGE_PASSWORD) {
        sessionStorage.setItem('algoConditionsUnlocked', '1');
        setUnlocked(true);
      } else {
        setPwError('Incorrect password. Please try again.');
        setPwInput('');
      }
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Algo Conditions</h2>
          <p className="text-sm text-gray-500 mb-6">Enter the password to access this page</p>
          <div className="relative mb-3">
            <input
              type={pwVisible ? 'text' : 'password'}
              value={pwInput}
              onChange={(e) => { setPwInput(e.target.value); setPwError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
              placeholder="Password"
              autoFocus
              className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 text-center tracking-widest"
            />
            <button
              type="button"
              onClick={() => setPwVisible((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            >
              {pwVisible ? '🙈' : '👁'}
            </button>
          </div>
          {pwError && (
            <p className="text-red-600 text-xs mb-3 font-semibold">{pwError}</p>
          )}
          <button
            onClick={handleUnlock}
            className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition text-sm"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-3 space-y-3">

      {/* ── Popup ── */}
      {popup.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-5 py-3 rounded-lg shadow-2xl border-2 flex items-center gap-3 font-semibold text-sm ${
            popup.type === 'success'
              ? 'bg-green-50 border-green-500 text-green-800'
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <span className="text-xl">{popup.type === 'success' ? '✅' : '❌'}</span>
            {popup.msg}
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl px-5 py-4 flex flex-wrap items-center justify-between gap-3 shadow-md">
        <div>
          <h1 className="text-white text-xl font-bold">⚙️ Algo Conditions</h1>
          <p className="text-indigo-200 text-xs mt-0.5">
            Monitor QOE table values from Algo Trading — auto-execute orders when conditions are met
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openAdd}
            className="bg-white text-indigo-700 font-bold px-4 py-1.5 rounded-lg text-sm shadow hover:bg-indigo-50 transition"
          >
            + Add Condition
          </button>
        </div>
      </div>

      {/* ── Two-column layout: live QOE preview + stats ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

        {/* Live QOE preview card */}
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 shadow overflow-hidden">
          <div className={`px-4 py-2 flex items-center justify-between ${qoeFresh ? 'bg-green-600' : 'bg-gray-500'}`}>
            <span className="text-white font-semibold text-sm flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${qoeFresh ? 'bg-white animate-pulse' : 'bg-gray-300'}`} />
              Live QOE Values
              {qoe && (
                <span className="text-xs font-normal opacity-80">
                  — {qoe.activeExchange} {qoe.activeSymbol || '—'}
                  {qoeAge !== null && ` · ${qoeAge}s ago`}
                </span>
              )}
            </span>
            {!qoe && (
              <span className="text-xs text-white/70">Open Algo Trading page to see live values</span>
            )}
          </div>

          <div className="p-3">
            {!qoeFresh && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mb-3">
                ⚠️ Live values are stale or unavailable. Keep the <strong>Algo Trading</strong> page open in another tab to stream QOE data here.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2">
              {['CE', 'PE'].map((row) => {
                const r = row.toLowerCase();
                const strengthVal = qoe?.[`${r}Strength`];
                const ltpVal      = qoe?.[`${r}LTP`];
                const difVal      = qoe?.[`${r}Dif`];

                return (
                  <div key={row} className={`rounded-lg border p-3 ${row === 'CE' ? 'border-blue-200 bg-blue-50' : 'border-orange-200 bg-orange-50'}`}>
                    <div className={`text-xs font-bold mb-2 ${row === 'CE' ? 'text-blue-700' : 'text-orange-700'}`}>
                      {row} Row
                    </div>
                    <div className="space-y-1 text-xs">
                      {[
                        { col: 'Strength', val: strengthVal, fmt: (v) => v === null ? '—' : `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(1)}%`, cls: (v) => v === null ? 'text-gray-400' : v >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold' },
                        { col: 'LTP',      val: ltpVal,      fmt: (v) => v === null ? '—' : `₹${parseFloat(v).toFixed(2)}`, cls: () => 'text-gray-800 font-semibold' },
                        { col: 'Dif',      val: difVal,      fmt: (v) => v === null ? '—' : `${v >= 0 ? '+' : ''}₹${parseFloat(v).toFixed(2)}`, cls: (v) => v === null ? 'text-gray-400' : v >= 0 ? 'text-green-600' : 'text-red-600' },
                      ].map(({ col, val, fmt, cls }) => (
                        <div key={col} className="flex justify-between items-center">
                          <span className="text-gray-500">{col}</span>
                          <span className={cls(val)}>{fmt(val)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-2">
          {[
            { label: 'Total',     value: stats.total,     color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
            { label: 'Watching',  value: stats.watching,  color: 'bg-yellow-50 border-yellow-200 text-yellow-800' },
            { label: 'Triggered', value: stats.triggered, color: 'bg-blue-50 border-blue-200 text-blue-800'       },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl border px-4 py-3 flex items-center justify-between ${s.color}`}>
              <span className="text-sm font-semibold">{s.label}</span>
              <span className="text-2xl font-bold">{s.value}</span>
            </div>
          ))}
          {/* Algo status */}
          <div className={`rounded-xl border px-4 py-3 text-xs font-medium flex items-center gap-2 ${
            algoEnabled ? 'bg-green-50 border-green-300 text-green-800' : 'bg-amber-50 border-amber-300 text-amber-800'
          }`}>
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${algoEnabled ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`} />
            {algoEnabled ? 'Auto-execute ON' : 'Auto-execute OFF'}
          </div>
        </div>
      </div>

      {/* ── Conditions table ── */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-4 py-2 flex items-center justify-between">
          <span className="text-white font-semibold text-sm">Conditions</span>
          <div className="flex items-center gap-2">
            {loading && <span className="text-indigo-200 text-xs animate-pulse">Loading…</span>}
            <button
              onClick={() => { setShowRefModal(true); loadReference(); }}
              className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 bg-white/10 border-white/30 text-white hover:bg-white/20 transition-all"
              title="Browse and copy conditions from other users"
            >
              📋 Reference
            </button>
            <div className="relative">
              <button
                onClick={toggleAlgo}
                title={algoEnabled ? 'Algo is ON — click to disable' : 'Click to enable Algo and choose mode'}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border-2 shadow transition-all ${
                  algoEnabled
                    ? 'bg-green-500 border-green-300 text-white hover:bg-green-600'
                    : 'bg-gray-700 border-gray-500 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${algoEnabled ? 'bg-white animate-pulse' : 'bg-gray-400'}`} />
                {algoEnabled ? `ALGO ON (${algoMode === 'BOTH' ? 'Both' : algoMode === 'BUY' ? 'Buy' : 'Sell'})` : 'ALGO OFF'}
              </button>
              {showAlgoModeDropdown && !algoEnabled && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 min-w-[170px] overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                    Enable Algo for…
                  </div>
                  {[
                    { mode: 'BUY',  label: '📈 Buy Conditions',  cls: 'text-green-700 hover:bg-green-50' },
                    { mode: 'SELL', label: '📉 Sell Conditions', cls: 'text-red-700 hover:bg-red-50' },
                    { mode: 'BOTH', label: '🔄 Both',             cls: 'text-indigo-700 hover:bg-indigo-50' },
                  ].map(({ mode, label, cls }) => (
                    <button
                      key={mode}
                      onClick={() => enableAlgoWithMode(mode)}
                      className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors ${cls}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {conditions.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">⚙️</div>
            <p className="font-semibold">No conditions yet</p>
            <p className="text-xs mt-1">Click <strong>+ Add Condition</strong> to create your first rule</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wide text-[10px]">
                  {['Name', 'Type', 'Exchange / Symbol', 'Row', 'Rule', 'Current Value', 'Action', 'Order', 'Status', 'Active', ''].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {conditions.map((cond) => {
                  const current = getCurrentValue(cond);
                  const badge   = statusBadge(cond);
                  const threshold = parseFloat(cond.trigger_value);
                  const met     = current !== null && evalCondition(current, cond.operator, threshold);
                  const nearMiss = current !== null && !met && (
                    (cond.operator === '<=' || cond.operator === '<')  ? current <= threshold * 1.05 :
                    (cond.operator === '>=' || cond.operator === '>')  ? current >= threshold * 0.95 : false
                  );

                  return (
                    <tr
                      key={cond.id}
                      className={`border-b border-gray-100 transition-colors ${
                        cond.is_triggered     ? 'bg-blue-50'
                        : met && cond.is_active ? 'bg-red-50 animate-pulse'
                        : nearMiss && cond.is_active ? 'bg-yellow-50'
                        : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Name */}
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-800">{cond.name}</div>
                        {cond.is_triggered && cond.triggered_symbol && (
                          <div className="text-purple-600 text-[10px] font-semibold">
                            👁 Watching exit: {cond.triggered_symbol}
                          </div>
                        )}
                        {cond.is_triggered && cond.order_id && (
                          <div className="text-blue-500 text-[10px]">Order: {cond.order_id}</div>
                        )}
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {cond.sl_enabled && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold border ${
                              cond.sl_fired
                                ? 'bg-red-600 text-white border-red-700'
                                : 'bg-red-50 text-red-700 border-red-300'
                            }`}>
                              {cond.sl_fired ? '🛑 SL Fired' : `🛑 SL ${cond.sl_operator}${cond.sl_value}${colUnit(cond.sl_column)}`}
                            </span>
                          )}
                          {cond.profit_enabled && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold border ${
                              cond.profit_fired
                                ? 'bg-green-600 text-white border-green-700'
                                : 'bg-green-50 text-green-700 border-green-300'
                            }`}>
                              {cond.profit_fired ? '💰 Profit Fired' : `💰 P ${cond.profit_operator}${cond.profit_value}${colUnit(cond.profit_column)}`}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Condition Type */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded border font-bold text-[10px] ${
                          (cond.condition_type || 'BOTH') === 'BUY'
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : (cond.condition_type || 'BOTH') === 'SELL'
                            ? 'bg-red-100 text-red-700 border-red-300'
                            : 'bg-indigo-50 text-indigo-600 border-indigo-200'
                        }`}>
                          {cond.condition_type || 'BOTH'}
                        </span>
                      </td>

                      {/* Exchange / Symbol */}
                      <td className="px-3 py-2">
                        <span className="font-bold text-indigo-700">{cond.exchange}</span>
                        <span className="text-gray-500 ml-1">/ {cond.symbol}</span>
                      </td>

                      {/* Row type */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded font-bold border text-[11px] ${
                          cond.row_type === 'CE'
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-orange-100 text-orange-700 border-orange-300'
                        }`}>
                          {cond.row_type}
                        </span>
                      </td>

                      {/* Rule: column operator value */}
                      <td className="px-3 py-2 font-mono text-gray-700 whitespace-nowrap">
                        <span className="font-semibold text-gray-900">{colLabel(cond.column_name)}</span>
                        {' '}
                        <span className="text-indigo-600 font-bold">{cond.operator}</span>
                        {' '}
                        <span className="text-gray-800">{cond.trigger_value}{colUnit(cond.column_name)}</span>
                      </td>

                      {/* Current value */}
                      <td className="px-3 py-2 font-mono">
                        {current !== null ? (
                          <span className={`font-bold ${
                            met ? 'text-red-600' : nearMiss ? 'text-orange-500' : 'text-gray-700'
                          }`}>
                            {fmtQoeVal(current, cond.column_name)}
                            {met && ' 🔥'}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">
                            {qoeFresh
                              ? `Needs ${cond.exchange} ${cond.symbol}`
                              : 'No data'}
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded border font-bold text-[11px] ${
                          cond.action === 'BUY'
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-red-100 text-red-700 border-red-300'
                        }`}>
                          {cond.action}
                        </span>
                      </td>

                      {/* Order type / product / cooldown */}
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        <div>{cond.order_type === 'MARKET' ? 'S-MKT' : 'LMT'} / {cond.product}</div>
                        {cond.cooldown_seconds > 0 && (
                          <div className="text-[10px] text-amber-600 font-semibold">
                            ⏱ {cond.cooldown_seconds}s cooldown
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded border text-[11px] font-semibold ${badge.cls}`}>
                          {badge.text}
                        </span>
                        {cond.is_triggered && cond.triggered_at && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            {new Date(cond.triggered_at).toLocaleTimeString()}
                          </div>
                        )}
                        {cond.is_triggered && cond.cooldown_seconds > 0 && cond.triggered_at && (() => {
                          const elapsed = (Date.now() - new Date(cond.triggered_at).getTime()) / 1000;
                          const remaining = Math.ceil(cond.cooldown_seconds - elapsed);
                          return remaining > 0 ? (
                            <div className="text-[10px] text-amber-600 font-semibold mt-0.5">
                              ⏱ cooldown {remaining}s
                            </div>
                          ) : null;
                        })()}
                      </td>

                      {/* Active toggle */}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleActive(cond)}
                          disabled={!!cond.is_triggered}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors ${
                            cond.is_triggered ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
                          } ${cond.is_active ? 'bg-green-500 border-green-600' : 'bg-gray-300 border-gray-400'}`}
                        >
                          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${cond.is_active ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {cond.is_triggered && (
                            <button onClick={() => resetCondition(cond)}
                              className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[11px] font-semibold hover:bg-blue-200">
                              Reset
                            </button>
                          )}
                          <button onClick={() => openEdit(cond)}
                            className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-[11px] font-semibold hover:bg-indigo-200">
                            Edit
                          </button>
                          <button onClick={() => deleteCondition(cond.id)}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-[11px] font-semibold hover:bg-red-200">
                            Del
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Execution Logs ── */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <span className="text-white font-semibold text-sm flex items-center gap-2">
            📋 Algo Trading Executed Logs
            {logsLoading && <span className="text-gray-400 text-xs animate-pulse">Loading…</span>}
          </span>

          <div className="flex items-center gap-2">
            {/* Filter tabs */}
            {['today', 'week', 'all'].map((f) => (
              <button
                key={f}
                onClick={() => setLogFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                  logFilter === f
                    ? 'bg-white text-gray-900'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {f === 'today' ? 'Today' : f === 'week' ? 'This Week' : 'All'}
              </button>
            ))}

            {/* Refresh */}
            <button
              onClick={() => fetchLogs(logFilter)}
              className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-gray-700"
              title="Refresh"
            >
              ↻
            </button>

            {/* Clear */}
            <button
              onClick={async () => {
                if (!window.confirm(`Clear ${logFilter === 'today' ? "today's" : logFilter === 'week' ? "this week's" : 'all'} logs?`)) return;
                try {
                  await api.delete(`/algo-conditions/logs?userId=${userId}&filter=${logFilter}`);
                  setExecLogs([]);
                } catch (_) {}
              }}
              className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-gray-700"
            >
              Clear
            </button>
          </div>
        </div>

        {execLogs.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <div className="text-3xl mb-2">📋</div>
            <p className="text-sm font-medium">No execution logs</p>
            <p className="text-xs mt-1">
              {logFilter === 'today' ? "No orders were auto-executed today." : "No logs found for the selected period."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr className="text-gray-500 uppercase tracking-wide text-[10px]">
                  {['Date & Time', 'Condition', 'Exchange / Symbol', 'Row', 'Rule Triggered', 'Value at Trigger', 'Action', 'Qty', 'Symbol Ordered', 'Order ID', 'Result'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {execLogs.map((e, i) => {
                  const isSuccess = e.status === 'SUCCESS';
                  const dt = e.executed_at ? new Date(e.executed_at) : null;
                  return (
                    <tr key={e.id ?? i} className={`border-b border-gray-100 ${isSuccess ? 'hover:bg-green-50' : 'bg-red-50 hover:bg-red-100'}`}>
                      {/* Date & Time */}
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {dt ? (
                          <>
                            <div className="font-semibold text-gray-700">{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                            <div>{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                          </>
                        ) : e.time ?? '—'}
                      </td>

                      {/* Condition name */}
                      <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                        {e.condition_name ?? e.name}
                      </td>

                      {/* Exchange / Symbol */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-bold text-indigo-700">{e.exchange}</span>
                        <span className="text-gray-500 ml-1">/ {e.symbol}</span>
                      </td>

                      {/* Row type */}
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded font-bold border text-[11px] ${
                          (e.row_type ?? e.rowType) === 'CE'
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-orange-100 text-orange-700 border-orange-300'
                        }`}>
                          {e.row_type ?? e.rowType}
                        </span>
                      </td>

                      {/* Rule: col operator triggerValue */}
                      <td className="px-3 py-2 font-mono whitespace-nowrap text-gray-700">
                        <span className="font-semibold text-gray-900">{colLabel(e.column_name ?? e.col)}</span>
                        {' '}
                        <span className="text-indigo-600 font-bold">{e.operator}</span>
                        {' '}
                        <span>{e.trigger_value ?? e.triggerValue}{(e.column_name ?? e.col) === 'strength' ? '%' : '₹'}</span>
                      </td>

                      {/* Actual value at trigger */}
                      <td className="px-3 py-2 font-mono font-bold">
                        {e.column_value != null
                          ? <span className={parseFloat(e.column_value) >= 0 ? 'text-green-700' : 'text-red-700'}>
                              {(e.column_name ?? e.col) === 'strength'
                                ? `${parseFloat(e.column_value) >= 0 ? '+' : ''}${parseFloat(e.column_value).toFixed(1)}%`
                                : `₹${parseFloat(e.column_value).toFixed(2)}`}
                            </span>
                          : e.value ?? '—'}
                      </td>

                      {/* Action */}
                      <td className="px-3 py-2">
                        {(e.action && e.action !== '— auto-reset') ? (
                          <span className={`px-2 py-0.5 rounded border font-bold text-[11px] ${
                            e.action === 'BUY'
                              ? 'bg-green-100 text-green-700 border-green-300'
                              : 'bg-red-100 text-red-700 border-red-300'
                          }`}>
                            {e.action}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-[11px]">♻️ Reset</span>
                        )}
                      </td>

                      {/* Qty */}
                      <td className="px-3 py-2 text-gray-700">{e.quantity ?? e.qty ?? '—'}</td>

                      {/* Symbol ordered */}
                      <td className="px-3 py-2 text-gray-600 font-mono whitespace-nowrap">
                        {e.trading_symbol ?? e.tradingSymbol ?? '—'}
                      </td>

                      {/* Order ID */}
                      <td className="px-3 py-2 text-gray-500 font-mono">
                        {e.order_id ?? e.orderId ?? '—'}
                      </td>

                      {/* Result */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isSuccess
                          ? <span className="text-green-700 font-semibold flex items-center gap-1">✅ Placed</span>
                          : <span className="text-red-600 font-semibold" title={e.error_message ?? e.error}>❌ Failed</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer summary */}
        {execLogs.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <span>
              {execLogs.filter((e) => e.status === 'SUCCESS').length} successful &nbsp;·&nbsp;
              {execLogs.filter((e) => e.status === 'FAILED').length} failed &nbsp;·&nbsp;
              {execLogs.length} total
            </span>
            <span className="text-gray-400">Showing {logFilter === 'today' ? "today's" : logFilter === 'week' ? "last 7 days'" : 'all'} records</span>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ADD / EDIT MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      {/* ── Reference Conditions Modal ── */}
      {showRefModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRefModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-5 py-3 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-white font-bold text-base">📋 Reference Conditions</h2>
                <p className="text-indigo-200 text-xs mt-0.5">Browse conditions from other users and copy what you need</p>
              </div>
              <button onClick={() => setShowRefModal(false)} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {refLoading ? (
                <div className="text-center py-16 text-gray-400 animate-pulse">Loading…</div>
              ) : refConditions.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <div className="text-4xl mb-3">📋</div>
                  <p className="font-semibold">No reference conditions found</p>
                  <p className="text-xs mt-1">Other users haven't created any conditions yet</p>
                </div>
              ) : (() => {
                // Group by user_id
                const grouped = refConditions.reduce((acc, c) => {
                  if (!acc[c.user_id]) acc[c.user_id] = [];
                  acc[c.user_id].push(c);
                  return acc;
                }, {});
                return Object.entries(grouped).map(([uid, conds]) => (
                  <div key={uid} className="mb-5">
                    <div className="text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5 mb-2">
                      👤 User: {uid} &nbsp;·&nbsp; {conds.length} condition{conds.length > 1 ? 's' : ''}
                    </div>
                    <div className="space-y-2">
                      {conds.map((cond) => {
                        const alreadyCopied = conditions.some(
                          (c) => c.name === cond.name && c.exchange === cond.exchange &&
                                 c.symbol === cond.symbol && c.row_type === cond.row_type &&
                                 c.column_name === cond.column_name && c.operator === cond.operator &&
                                 parseFloat(c.trigger_value) === parseFloat(cond.trigger_value) && c.action === cond.action
                        );
                        return (
                          <div key={cond.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-800 text-sm truncate">{cond.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                <span className="font-bold text-indigo-700">{cond.exchange}</span>
                                <span className="text-gray-400"> / </span>
                                <span>{cond.symbol}</span>
                                <span className={`ml-2 px-1.5 py-0.5 rounded font-bold text-[10px] border ${cond.row_type === 'CE' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-orange-100 text-orange-700 border-orange-300'}`}>{cond.row_type}</span>
                                <span className="ml-2 font-mono">{cond.column_name} {cond.operator} {cond.trigger_value}{cond.column_name === 'strength' ? '%' : '₹'}</span>
                                <span className={`ml-2 px-1.5 py-0.5 rounded font-bold text-[10px] border ${cond.action === 'BUY' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}>{cond.action}</span>
                                {cond.sl_enabled && <span className="ml-1 text-[10px] text-red-600">🛑SL</span>}
                                {cond.profit_enabled && <span className="ml-1 text-[10px] text-green-600">💰P</span>}
                                {cond.cooldown_seconds > 0 && <span className="ml-1 text-[10px] text-amber-600">⏱{cond.cooldown_seconds}s</span>}
                              </div>
                            </div>
                            <button
                              onClick={() => copyCondition(cond)}
                              disabled={!!alreadyCopied || copyingId === cond.id}
                              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                alreadyCopied
                                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                  : copyingId === cond.id
                                  ? 'bg-indigo-100 text-indigo-400 border border-indigo-200 cursor-wait'
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
                              }`}
                            >
                              {alreadyCopied ? '✓ Copied' : copyingId === cond.id ? 'Copying…' : 'Copy'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>

            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
              <span className="text-xs text-gray-500">{refConditions.length} condition{refConditions.length !== 1 ? 's' : ''} from other users</span>
              <button onClick={() => setShowRefModal(false)} className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300">Close</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          {/* Drawer panel */}
          <div className="relative w-[60%] h-full bg-white shadow-2xl flex flex-col overflow-hidden">

            {/* Drawer header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-white font-bold text-lg">
                {editingId ? 'Edit Condition' : 'New Condition'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Condition Name *</label>
                <input
                  type="text"
                  placeholder="e.g. NIFTY CE Strength buy signal"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Condition Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Condition Type *</label>
                <div className="flex gap-2">
                  {CONDITION_TYPES.map((ct) => (
                    <button
                      key={ct.value}
                      type="button"
                      title={ct.hint}
                      onClick={() => setForm((f) => ({ ...f, conditionType: ct.value }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold border transition ${
                        form.conditionType === ct.value
                          ? ct.value === 'BUY'
                            ? 'bg-green-600 text-white border-green-600'
                            : ct.value === 'SELL'
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {ct.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  {CONDITION_TYPES.find((ct) => ct.value === form.conditionType)?.hint}
                </p>
              </div>

              {/* Exchange */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Exchange *</label>
                <div className="flex gap-2 flex-wrap">
                  {EXCHANGES.map((ex) => (
                    <button
                      key={ex}
                      onClick={() => setForm((f) => ({ ...f, exchange: ex, symbol: SYMBOLS_BY_EXCHANGE[ex]?.[0] || '' }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                        form.exchange === ex
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>

              {/* Symbol */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Symbol *</label>
                <div className="flex gap-2 flex-wrap">
                  {(SYMBOLS_BY_EXCHANGE[form.exchange] || []).map((sym) => (
                    <button
                      key={sym}
                      onClick={() => setForm((f) => ({ ...f, symbol: sym }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                        form.symbol === sym
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {sym}
                    </button>
                  ))}
                  {/* Free-type for NSE or custom */}
                  {(form.exchange === 'NSE' || form.symbol === 'CUSTOM') && (
                    <input
                      type="text"
                      placeholder="Symbol name"
                      value={form.symbol === 'CUSTOM' ? '' : form.symbol}
                      onChange={(e) => setForm((f) => ({ ...f, symbol: e.target.value.toUpperCase() }))}
                      className="border border-gray-300 rounded-full px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    />
                  )}
                </div>
              </div>

              {/* Row: CE / PE */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Row (QOE Table) *</label>
                <div className="flex gap-2">
                  {['CE', 'PE'].map((r) => (
                    <button
                      key={r}
                      onClick={() => setForm((f) => ({ ...f, rowType: r }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-bold border transition ${
                        form.rowType === r
                          ? r === 'CE'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {r} Row
                    </button>
                  ))}
                </div>
              </div>

              {/* Column */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Column (Monitor) *</label>
                <div className="grid grid-cols-3 gap-2">
                  {COLUMNS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setForm((f) => ({ ...f, columnName: c.value }))}
                      title={c.hint}
                      className={`py-2 rounded-lg text-xs font-bold border text-center transition ${
                        form.columnName === c.value
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {c.label}
                      <span className="text-[10px] block opacity-70">{c.unit}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  {COLUMNS.find((c) => c.value === form.columnName)?.hint}
                </p>
              </div>

              {/* Operator + Trigger value */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Operator *</label>
                  <select
                    value={form.operator}
                    onChange={(e) => setForm((f) => ({ ...f, operator: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Trigger Value * {form.columnName === 'strength' ? '(%)' : '(₹)'}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder={form.columnName === 'strength' ? '-30 or 15' : '150.00'}
                    value={form.triggerValue}
                    onChange={(e) => setForm((f) => ({ ...f, triggerValue: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>

              {/* Summary preview */}
              {form.name && form.triggerValue && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 text-sm">
                  <span className="font-semibold text-indigo-700">When </span>
                  <span className="text-gray-800">
                    {form.exchange} {form.symbol} → {form.rowType} row → <strong>{colLabel(form.columnName)}</strong> {form.operator} <strong>{form.triggerValue}{colUnit(form.columnName)}</strong>
                  </span>
                  <span className="font-semibold text-indigo-700"> → </span>
                  <span className={`font-bold ${form.action === 'BUY' ? 'text-green-700' : 'text-red-700'}`}>
                    {form.action}
                  </span>
                  <span className="text-gray-600"> using {form.orderType === 'MARKET' ? 'S-MKT' : 'LMT'} / {form.product}</span>
                </div>
              )}

              {/* Action */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Execute Action *</label>
                <div className="flex gap-2">
                  {['BUY', 'SELL'].map((a) => (
                    <button
                      key={a}
                      onClick={() => setForm((f) => ({ ...f, action: a, conditionType: a }))}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-bold border transition ${
                        form.action === a
                          ? a === 'BUY'
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  Order will be placed using the qty, order type, and product from the QOE table in Algo Trading.
                </p>
              </div>

              {/* ── Stop Loss ── */}
              <div className="border border-red-200 rounded-xl overflow-hidden">
                <label className="flex items-center gap-2 cursor-pointer select-none bg-red-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={form.slEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, slEnabled: e.target.checked }))}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span className="text-sm font-bold text-red-700">🛑 Stop Loss</span>
                  <span className="text-xs text-red-500 ml-1">— exit position when condition is met</span>
                </label>
                {form.slEnabled && (
                  <div className="px-4 pb-4 pt-2 bg-red-50 space-y-3">
                    <p className="text-xs text-red-600 bg-red-100 border border-red-200 rounded px-2 py-1">
                      If the main condition has already fired (position is open), and this rule is met → execute <strong>{form.action === 'BUY' ? 'SELL' : 'BUY'}</strong> to exit.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Column</label>
                        <select value={form.slColumn} onChange={(e) => setForm((f) => ({ ...f, slColumn: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-red-400">
                          {COLUMNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Operator</label>
                        <select value={form.slOperator} onChange={(e) => setForm((f) => ({ ...f, slOperator: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-red-400">
                          {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Value {form.slColumn === 'strength' ? '(%)' : '(₹)'}
                        </label>
                        <input type="number" step="0.1"
                          placeholder={form.slColumn === 'strength' ? '10' : '150'}
                          value={form.slValue}
                          onChange={(e) => setForm((f) => ({ ...f, slValue: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-red-400"
                        />
                      </div>
                    </div>
                    {form.slValue !== '' && (
                      <div className="text-xs text-red-700 bg-white border border-red-200 rounded px-2 py-1 font-mono">
                        SL exits when {colLabel(form.slColumn)} {form.slOperator} {form.slValue}{colUnit(form.slColumn)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Profit Booking ── */}
              <div className="border border-green-200 rounded-xl overflow-hidden">
                <label className="flex items-center gap-2 cursor-pointer select-none bg-green-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={form.profitEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, profitEnabled: e.target.checked }))}
                    className="w-4 h-4 accent-green-600"
                  />
                  <span className="text-sm font-bold text-green-700">💰 Profit Booking</span>
                  <span className="text-xs text-green-600 ml-1">— exit position when profit target is met</span>
                </label>
                {form.profitEnabled && (
                  <div className="px-4 pb-4 pt-2 bg-green-50 space-y-3">
                    <p className="text-xs text-green-700 bg-green-100 border border-green-200 rounded px-2 py-1">
                      If the main condition has already fired (position is open), and this rule is met → execute <strong>{form.action === 'BUY' ? 'SELL' : 'BUY'}</strong> to exit. SL and Profit are mutually exclusive — whichever fires first wins.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Column</label>
                        <select value={form.profitColumn} onChange={(e) => setForm((f) => ({ ...f, profitColumn: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-400">
                          {COLUMNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Operator</label>
                        <select value={form.profitOperator} onChange={(e) => setForm((f) => ({ ...f, profitOperator: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-400">
                          {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          Value {form.profitColumn === 'strength' ? '(%)' : '(₹)'}
                        </label>
                        <input type="number" step="0.1"
                          placeholder={form.profitColumn === 'strength' ? '-100' : '300'}
                          value={form.profitValue}
                          onChange={(e) => setForm((f) => ({ ...f, profitValue: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-400"
                        />
                      </div>
                    </div>
                    {form.profitValue !== '' && (
                      <div className="text-xs text-green-700 bg-white border border-green-200 rounded px-2 py-1 font-mono">
                        Profit exits when {colLabel(form.profitColumn)} {form.profitOperator} {form.profitValue}{colUnit(form.profitColumn)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Order type + Product overrides */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Order Type Override</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'MARKET', label: 'S-MKT', title: 'Smart Market: converted to LIMIT order with buffer on backend' },
                      { value: 'LIMIT',  label: 'LMT',   title: 'Standard LIMIT order at specified price' },
                    ].map(({ value, label, title }) => (
                      <button key={value} title={title} onClick={() => setForm((f) => ({ ...f, orderType: value }))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition ${
                          form.orderType === value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                        }`}>{label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Product Override</label>
                  <div className="flex gap-2">
                    {['MIS', 'NRML'].map((p) => (
                      <button key={p} onClick={() => setForm((f) => ({ ...f, product: p }))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition ${
                          form.product === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                        }`}>{p}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Trigger once */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.triggerOnce}
                  onChange={(e) => setForm((f) => ({ ...f, triggerOnce: e.target.checked }))}
                  className="w-4 h-4 accent-indigo-600"
                />
                <span className="text-sm text-gray-700">
                  Trigger once — stop monitoring after the first order fires
                </span>
              </label>

              {/* Cooldown */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Cooldown (seconds)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0 = no cooldown"
                    value={form.cooldownSeconds}
                    onChange={(e) => setForm((f) => ({ ...f, cooldownSeconds: e.target.value }))}
                    className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <span className="text-xs text-gray-500">seconds</span>
                  {[30, 60, 120, 300].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, cooldownSeconds: s }))}
                      className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                        parseInt(form.cooldownSeconds) === s
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {s >= 60 ? `${s / 60}m` : `${s}s`}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  After an order fires, the condition will not re-arm for this many seconds — prevents duplicate orders while AliceBlue position data is still updating.
                </p>
              </div>

              {formErr && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded-lg text-sm">{formErr}</div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCondition}
                  disabled={saving}
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 disabled:opacity-60 transition"
                >
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Create Condition'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default AlgoConditions;
